import { CustomerIndexViewModel } from '@ail/customers-api'
import ExcellentExport, { ConvertOptions } from 'excellentexport'
import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DataGrid, {
  CellClickArgs,
  Column,
  RowHeightArgs,
  SortColumn,
  SortDirection,
  TreeDataGrid,
} from 'react-data-grid'
import { Arr } from '../../../helpers/Arr'
import { useSettings } from '../../../hooks/useSettings'
import { DriverFilter } from '../../../types/enums'
import { Filter } from './Filter/Filter'
import { FilterModal } from './FilterModal/FilterModal'
import './Grid.scss'
import { GridOptions } from './GridOptions/GridOptions'
import { Pagination } from './Pagination/Pagination'
import { useTranslation } from 'react-i18next'
import DataSecurityPopup from '../../ContractDetails/Popups/DataSecurityPopup'
import { Matomo } from '../../../helpers/Matomo'
import { SelectionState } from '../../GeneralContracts/ReplacementState/ReplacementStateSelector'

export interface GridFilter<T, U, V> {
  type: string
  filterFn?: (item: T, filterValue: U) => boolean | undefined
  format?: (value?: U) => string | string[] | undefined
  debounce?: number
  data?: V
  value?: U
  onChange?: (value?: U) => void
  /**
   * Position of the filter UI in the grid layout.
   *
   * - `'column'` - Renders the filter in the column header or cell area.
   * - `'header-left'` - Places the filter in a shared header section aligned to the left.
   * - `'header-right'` - Places the filter in a shared header section aligned to the right.
   * - `'header-center'` - Centers the filter in a shared header section.
   * - `'none'` - Does not render a visible filter UI element and disables filter entirely.
   */
  position?: 'column' | 'header-left' | 'header-right' | 'header-center' | 'none'
  readOnly?: boolean
  cascadeClear?: string | string[]
  autoTrigger?: boolean
  ignoreSaved?: boolean
  controlled?: boolean
  pending?: boolean
  customClass?: string
  customPlaceholder?: string
}

export interface InlineTextFilter<T> extends GridFilter<T, string, undefined> {
  type: 'inline_text_filter'
}

export interface ModalDeliveryCustomerFilter<T> extends GridFilter<T, string[], CustomerIndexViewModel[]> {
  type: 'modal_delivery_customer_filter'
}

export interface ModalContractsDropdownFilter<T> extends GridFilter<T, string[], string[]> {
  type: 'modal_contracts_dropdown_filter'
  title: string
  transPrefix?: string
}

export interface ModalDriverFilter<T> extends GridFilter<T, DriverFilter, undefined> {
  type: 'modal_driver_filter'
}

export interface ModalReplacementStateFilter<T> extends GridFilter<T, SelectionState, SelectionState> {
  type: 'modal_replacement_state_filter'
}

export interface InlineSelectorFilter<T> extends GridFilter<T, string | undefined, string[]> {
  type: 'inline_selector_filter'
  enableAllOption?: boolean
}
export interface InlineButtonFilter<T> extends GridFilter<T, boolean, undefined> {
  type: 'inline_button_filter'
  btnText?: string
  icon?: string
  conditionals?: {
    renderRight?: boolean
    on: { text?: string; icon?: string; class?: string }
    off: { text?: string; icon?: string; class?: string }
  }
}
export type ColumnFilter<T> =
  | InlineTextFilter<T>
  | InlineSelectorFilter<T>
  | ModalDeliveryCustomerFilter<T>
  | ModalDriverFilter<T>
  | ModalContractsDropdownFilter<T>
  | InlineButtonFilter<T>
  | ModalReplacementStateFilter<T>

export interface GridColumn<T> extends Column<T, Record<string, ColumnFilter<T> | undefined>> {
  /** Column label displayed in column settings modal. */
  label?: string
  sortBy?: (item: T) => string | number | Date | undefined
  groupBy?: (item: T) => string
  exportFormat?: (item: T) => string | undefined | null
  hide?: boolean
  remove?: boolean
  sortDirection?: SortDirection
  filter?: ColumnFilter<T>
  parentKey?: string
  additionalExportCols?: { label: string; value: (item: T) => string | undefined }[]
}

export interface GridProps<T> {
  id?: string
  title?: string
  icon?: string
  columns: GridColumn<T>[]
  rows: T[]
  style?: CSSProperties
  showTotalItems?: boolean
  pagination?: boolean
  hideFiltersBtn?: boolean
  hideReorder?: boolean
  hideExport?: boolean
  rowKeyGetter: (row: T) => string
  onCellDoubleClick?: (data: CellClickArgs<T, Record<string, ColumnFilter<T> | undefined>>) => void
  onReload?: () => void
  onExport?: (data: string[][]) => string[][] | undefined
  renderDetails?: (row: T) => ReactNode | T[]
  renderSubHeader?: (rows: T[]) => ReactNode
  detailsVisibility?: Record<string, number | undefined>
  rowClass?: (row: T, index: number) => string
  customClass?: string
  headerHeight?: number
  renderCustomOption?: () => ReactNode | ReactNode[]
  exportTitle?: string
}

export function Grid<T extends object>(props: GridProps<T>) {
  return <InnerGrid {...props} key={props.id}></InnerGrid>
}

function InnerGrid<T extends object>(props: GridProps<T>) {
  const settingsAppliedRef = useRef<boolean>(!props.id)
  const [prevColumns, setPrevColumns] = useState<GridColumn<T>[]>([])
  const [innerColumns, setInnerColumns] = useState<GridColumn<T>[]>([])
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [currentPage, setCurrentPage] = useState(0)
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [filterModalKey, setFilterModalKey] = useState<string>()
  const [saveRequest, setSaveRequest] = useState(0)
  const [selectedRowKey, setSelectedRowKey] = useState<string>()
  const [settings, updateSettings, settingsLoading] = useSettings('i4uGrids')
  const [pendingExportConfirm, setPendingExportConfirm] = useState<{ onConfirm: () => void }>()
  const [expandedGroupIds, setExpandedGroupIds] = useState((): ReadonlySet<unknown> => new Set<string>([]))
  const { t } = useTranslation('common')

  if (props.columns != prevColumns) {
    const result: GridColumn<T>[] = []
    let lastIdx = 0
    const addedColumns: Record<number, GridColumn<T> | undefined> = {}
    for (let i = 0; i < props.columns.length; i++) {
      if (props.columns[i].remove) {
        continue
      }
      const column: GridColumn<T> = {
        ...props.columns[i],
        sortable: !!props.columns[i].sortBy,
        renderGroupCell:
          props.columns[i].renderGroupCell ??
          (props.columns[i].groupBy ? (data) => data.groupKey as string : undefined),
        renderSummaryCell: (data) =>
          (data.row[data.column.key]?.position === 'column' || !data.row[data.column.key]?.position) && (
            <Filter
              columnKey={data.column.key}
              filters={data.row}
              pending={false}
              onChange={handleFilterChange}
              onModalOpen={setFilterModalKey}
            />
          ),
      }
      const idx = innerColumns.findIndex((c) => c.key === column.key)
      const currentColumn = idx > -1 ? innerColumns[idx] : undefined
      column.sortDirection = currentColumn?.sortDirection ?? column.sortDirection
      column.hide = currentColumn?.hide ?? column.hide
      if (column.filter) {
        column.filter = {
          ...column.filter,
          controlled: 'value' in column.filter,
        }
        if (column.filter.type === currentColumn?.filter?.type && !column.filter.controlled) {
          column.filter.value = currentColumn.filter.value
        }
      }
      if (!currentColumn) {
        addedColumns[lastIdx] = column
      } else {
        result.splice(idx, 0, column)
      }
      lastIdx++
    }
    Object.entries(addedColumns).forEach(([idx, col]) => result.splice(+idx, 0, col!))
    setPrevColumns(props.columns)
    setInnerColumns(result)
  }

  if (!settingsAppliedRef.current && !settingsLoading && innerColumns.length) {
    const saved = props.id ? settings[props.id] : undefined
    const result: GridColumn<T>[] = []
    for (let i = 0; i < innerColumns.length; i++) {
      const column: GridColumn<T> = { ...innerColumns[i] }
      const idx = saved?.columns.findIndex((c) => c.key === column.key) ?? -1
      const savedCol = idx > -1 ? saved?.columns[idx] : undefined
      column.sortDirection = savedCol?.sortDirection ?? column.sortDirection
      column.hide = savedCol?.hide ?? column.hide
      let triggerModal = column.filter?.autoTrigger && column.filter.value === undefined
      if (
        column.filter &&
        column.filter.type === savedCol?.filter?.type &&
        column.filter.value !== savedCol.filter.value &&
        !column.filter.ignoreSaved
      ) {
        column.filter = { ...column.filter }
        if (!column.filter.controlled) {
          column.filter.value = savedCol.filter.value as never
        }
        triggerModal = column.filter?.autoTrigger && savedCol.filter.value === undefined
        column.filter.onChange?.(savedCol?.filter?.value as never)
      }
      if (triggerModal) {
        setFilterModalKey(column.key)
      }
      result.splice(idx > -1 ? idx : i, 0, column)
    }
    setRowsPerPage(saved?.rowsPerPage ?? 0)
    setFiltersVisible(saved?.filtersVisible ?? false)
    setInnerColumns(result)
    settingsAppliedRef.current = true
    console.debug(`Grid: applied saved settings`, { result })
  }

  const rowKeyGetter = useCallback(
    (row: T) => {
      const rowKeyGetter = props.rowKeyGetter
      const key = rowKeyGetter(row)
      return '_detail_' in row ? `${key}_detail_` : key
    },
    [props.rowKeyGetter]
  )

  const handleFilterChange = useCallback((key: string, filter: ColumnFilter<T>) => {
    setInnerColumns((current) => {
      const columns = [...current]
      const idx = current.findIndex((c) => c.key === key)
      const column = idx > -1 ? columns[idx] : undefined
      if (column?.filter?.type === filter.type) {
        columns[idx] = {
          ...column,
          filter: { ...column.filter, value: filter.value as never },
        }
      }
      return columns
    })
    filter.onChange?.(filter.value as never)
    setSaveRequest((s) => s + 1)
  }, [])

  useEffect(() => {
    if (props.id && saveRequest > 0) {
      updateSettings({
        ...settings,
        [props.id]: {
          filtersVisible,
          rowsPerPage,
          columns: innerColumns.map((c) => ({
            key: c.key,
            filter: c.filter ? { type: c.filter.type, value: c.filter.value } : undefined,
            hide: c.hide,
            sortDirection: c.sortDirection,
          })),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequest])

  useEffect(() => {
    setRowsPerPage((current) => (props.pagination ? current : 0))
  }, [props.pagination])

  const updateSortColumns = (sortColumns: SortColumn[]) => {
    setInnerColumns((current) => {
      const updated = current.map((c) => ({
        ...c,
        sortDirection: sortColumns.slice(-1).find((sc) => sc.columnKey === c.key)?.direction,
      }))
      return updated
    })
    setSaveRequest((s) => s + 1)
  }

  const updateRowsPerPage = (count: number) => {
    setRowsPerPage(count)
    setSaveRequest((s) => s + 1)
  }

  const updateFilersVisible = (visible: boolean) => {
    setFiltersVisible(visible)
    setSaveRequest((s) => s + 1)
  }

  const updateColumns = (columns: GridColumn<T>[]) => {
    setInnerColumns(columns)
    setSaveRequest((s) => s + 1)
  }

  const sortColumns = useMemo(() => {
    return innerColumns.filter((c) => c.sortDirection).map((c) => ({ columnKey: c.key, direction: c.sortDirection! }))
  }, [innerColumns])

  const renderedColumns = useMemo(() => {
    let result: GridColumn<T>[] = []
    const childMap: Record<string, GridColumn<T>[] | undefined> = {}
    for (let i = 0; i < innerColumns.length; i++) {
      if (innerColumns[i].hide) {
        continue
      }
      const column = innerColumns[i]
      if (column.parentKey) {
        const children = childMap[column.parentKey] ?? []
        children.push(column)
        childMap[column.parentKey] = children
        continue
      }
      result.push(column)
    }
    for (const key in childMap) {
      const parentIdx = result.findIndex((c) => c.key === key)
      if (parentIdx > -1) {
        result[parentIdx] = {
          ...result[parentIdx],
          colSpan: (data) => (data.type === 'ROW' ? childMap[key]!.length + 1 : undefined),
        }
        result.splice(parentIdx + 1, 0, ...childMap[key]!)
      }
    }
    result = result.sort((a, b) => (b.groupBy ? 1 : -1) - (a.groupBy ? 1 : -1))
    const groupedCount = result.filter((c) => c.groupBy).length
    if (result[groupedCount] && props.renderDetails) {
      const renderCell = result[groupedCount].renderCell
      const colSpan = result[groupedCount].colSpan
      result[groupedCount] = {
        ...result[groupedCount],
        renderCell: (data) =>
          '_detail_' in data.row && data.row._detail_ === 'jsx'
            ? (props.renderDetails!(data.row) as ReactNode)
            : renderCell?.(data) ?? <>{data.row[data.column.key as keyof T]}</>,
        colSpan: (data) =>
          data.type === 'ROW' && '_detail_' in data.row && data.row._detail_ === 'jsx'
            ? result.length - groupedCount
            : colSpan?.(data),
      }
    }
    return result
  }, [innerColumns, props.renderDetails])

  const sortedFilteredRows = useMemo(() => {
    const start = performance.now()
    const filterColumns = innerColumns.filter(
      (c) => c.filter && c.filter.position !== 'none' && c.filter.filterFn && c.filter.value != null
    )
    let result: T[] = []
    for (let i = 0; i < props.rows.length; i++) {
      const row = props.rows[i]
      if (filterColumns.every((f) => f.filter!.filterFn!(row, f.filter!.value as never))) {
        result.push(row)
      }
    }

    const sortColumn = innerColumns.find((c) => c.sortDirection && c.sortBy)
    if (sortColumn) {
      result = Arr.sort(result, sortColumn.sortBy!, sortColumn.sortDirection!)
    }
    const end = performance.now()
    console.debug(`Grid: sort and filter rows ${end - start}ms`)
    return result
  }, [innerColumns, props.rows])

  const pageCount = useMemo(() => {
    if (rowsPerPage < 1 || !sortedFilteredRows.length) {
      return 1
    }
    return Math.ceil(sortedFilteredRows.length / rowsPerPage)
  }, [sortedFilteredRows, rowsPerPage])

  const validCurrentPage = currentPage + 1 > pageCount ? 0 : currentPage

  const renderedRows = useMemo(() => {
    const start = Math.min(validCurrentPage * rowsPerPage, sortedFilteredRows.length)
    const end = rowsPerPage == 0 ? sortedFilteredRows.length : Math.min(start + rowsPerPage, sortedFilteredRows.length)
    const result: T[] = []
    for (let i = start; i < end; i++) {
      const row = sortedFilteredRows[i]
      result.push(row)
      const key = rowKeyGetter(row)
      const renderDetails = props.renderDetails
      if (key && renderDetails && props.detailsVisibility?.[key]) {
        const details = renderDetails(row)
        if (Array.isArray(details)) {
          for (const detail of details) {
            result.push({ ...detail, _detail_: 'row' })
          }
        } else {
          result.push({ ...row, _detail_: 'jsx' })
        }
      }
    }
    return result
  }, [validCurrentPage, rowsPerPage, sortedFilteredRows, rowKeyGetter, props.renderDetails, props.detailsVisibility])

  const exportData = () => {
    Matomo.pushEvent('DataProtection', 'Export', props.id ?? 'Grid', window.location.href)
    const result: string[][] = []
    const exportColumns = renderedColumns.filter((c) => c.exportFormat)
    const headerRow = exportColumns.flatMap((c) => {
      const baseLabel = c.label ?? c.name.toString()
      const additionalLabels = c.additionalExportCols?.map((ac) => ac.label) ?? []
      return [baseLabel, ...additionalLabels]
    })
    result.push(headerRow)
    for (const row of sortedFilteredRows) {
      const values: string[] = []
      for (const column of exportColumns) {
        values.push(column.exportFormat!(row) ?? '')
        if (column.additionalExportCols) {
          column.additionalExportCols.forEach((add) => {
            values.push(add.value(row) ?? '')
          })
        }
      }
      result.push(values)
    }

    const data = props.onExport ? props.onExport(result) : result
    if (data) {
      const options: ConvertOptions = {
        openAsDownload: true,
        format: 'xlsx',
        filename: `${props.title ?? props.exportTitle}_${getExportTimeStamp()}`,
      }
      const sheet = {
        name: '1',
        from: {
          array: result,
          arrayHasHeader: true,
        },
      }
      ExcellentExport.convert(options, [sheet])
    }
  }

  const clearFilters = () => {
    const result: GridColumn<T>[] = []
    for (let i = 0; i < innerColumns.length; i++) {
      let column = innerColumns[i]
      if (column.filter?.value !== undefined && !column.filter.readOnly) {
        if (!column.filter.controlled) {
          column = { ...column, filter: { ...column.filter, value: undefined } }
        }
        column.filter?.onChange?.(undefined)
      }
      result.push(column)
    }
    setInnerColumns(result)
    setSaveRequest((s) => s + 1)
  }

  const rowClass = useCallback(
    (row: T, index: number) => {
      const classes: string[] = []
      const rowClass = props.rowClass
      if (rowClass) {
        classes.push(rowClass(row, index))
      }
      if ('_detail_' in row) {
        classes.push('detail-row')
      } else if (selectedRowKey === rowKeyGetter(row)) {
        classes.push('selected-row')
      }
      return classes.join(' ')
    },
    [props.rowClass, rowKeyGetter, selectedRowKey]
  )

  const rowHeight = useCallback(
    (args: T | RowHeightArgs<T>) => {
      const row = 'row' in args ? args.row : args
      if ('groupKey' in row) {
        return 41
      } else if ('_detail_' in row && row._detail_ === 'jsx') {
        const key = rowKeyGetter(row).split('_detail_')[0]
        return props.detailsVisibility?.[key] ?? 0
      }
      return 41
    },
    [props.detailsVisibility, rowKeyGetter]
  )

  const filterModalColumn = useMemo(() => {
    return filterModalKey ? innerColumns.find((c) => c.key === filterModalKey) : undefined
  }, [innerColumns, filterModalKey])

  const filters = useMemo(() => {
    return Arr.toHashMap(
      innerColumns,
      (c) => c.key,
      (c) => c.filter
    )
  }, [innerColumns])

  function getExportTimeStamp() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    return `${year}${month}${day}_${hours}${minutes}`
  }

  const grouping = useMemo(() => {
    const map: Record<string, GridColumn<T>> = {}
    const keys: string[] = []
    for (const column of renderedColumns) {
      if (!column.groupBy) continue
      map[column.key] = column
      keys.push(column.key)
    }
    return {
      keys,
      rowGrouper: (rows: readonly T[], columnKey: string) => {
        const result: Record<string, T[]> = {}
        const groupFn = map[columnKey].groupBy!
        for (const row of rows) {
          const key = groupFn(row)
          if (!result[key]) result[key] = []
          result[key].push(row)
        }
        return result
      },
    }
  }, [renderedColumns])

  return (
    <div className={`grid-component ${props.customClass ? props.customClass : ''}`}>
      <GridOptions
        title={props.title}
        icon={props.icon}
        columns={innerColumns}
        filters={filters}
        onFilterChange={handleFilterChange}
        onModalOpen={setFilterModalKey}
        defaultColumns={props.columns}
        pagination={props.pagination}
        totalItems={props.showTotalItems ? sortedFilteredRows.length : undefined}
        rowsPerPage={rowsPerPage}
        filtersVisible={filtersVisible}
        onClearFiltersClick={clearFilters}
        onRowsPerPageChange={updateRowsPerPage}
        onColumnsChange={updateColumns}
        onFiltersVisibleChange={updateFilersVisible}
        onExport={() => setPendingExportConfirm({ onConfirm: () => exportData() })}
        onReload={props.onReload}
        hideFiltersBtn={props.hideFiltersBtn}
        hideReorder={props.hideReorder}
        hideExport={props.hideExport}
        renderCustomOption={props.renderCustomOption}
      />
      {props.renderSubHeader && props.renderSubHeader(sortedFilteredRows)}
      {!sortedFilteredRows.length && <div className="grid-no-results-message">{t('NO_RESULTS_FOUND')}</div>}
      {!!grouping.keys.length && (
        <TreeDataGrid
          className="rdg-light"
          style={props.style}
          headerRowHeight={props.headerHeight ?? 45}
          columns={renderedColumns}
          sortColumns={sortColumns}
          rows={renderedRows}
          topSummaryRows={filtersVisible ? [filters] : []}
          onSortColumnsChange={updateSortColumns}
          onCellClick={(data) => setSelectedRowKey(rowKeyGetter(data.row))}
          onCellDoubleClick={props.onCellDoubleClick}
          rowClass={rowClass}
          rowKeyGetter={rowKeyGetter}
          rowHeight={rowHeight}
          renderers={{
            renderSortStatus: (p) => (
              <i
                style={{ paddingLeft: 5, fontSize: 10 }}
                className={
                  p.sortDirection === 'ASC'
                    ? 'icon-sort-up'
                    : p.sortDirection === 'DESC'
                    ? 'icon-sort-down'
                    : 'icon-sort'
                }
              />
            ),
          }}
          groupBy={grouping.keys}
          rowGrouper={grouping.rowGrouper}
          expandedGroupIds={expandedGroupIds}
          onExpandedGroupIdsChange={setExpandedGroupIds}
        />
      )}
      {!grouping.keys.length && (
        <DataGrid
          className="rdg-light"
          style={props.style}
          rowHeight={rowHeight}
          headerRowHeight={props.headerHeight ?? 45}
          columns={renderedColumns}
          sortColumns={sortColumns}
          rows={renderedRows}
          topSummaryRows={filtersVisible ? [filters] : []}
          onSortColumnsChange={updateSortColumns}
          onCellClick={(data) => setSelectedRowKey(rowKeyGetter(data.row))}
          onCellDoubleClick={props.onCellDoubleClick}
          rowClass={rowClass}
          rowKeyGetter={rowKeyGetter}
          renderers={{
            renderSortStatus: (p) => (
              <i
                style={{ paddingLeft: 5, fontSize: 10 }}
                className={
                  p.sortDirection === 'ASC'
                    ? 'icon-sort-up'
                    : p.sortDirection === 'DESC'
                    ? 'icon-sort-down'
                    : 'icon-sort'
                }
              />
            ),
          }}
        />
      )}
      <Pagination currentPage={validCurrentPage} pageCount={pageCount} onChange={setCurrentPage} />
      <FilterModal
        column={filterModalColumn}
        onChange={handleFilterChange}
        onClose={() => setFilterModalKey(undefined)}
      ></FilterModal>
      <DataSecurityPopup
        onConfirm={() => {
          pendingExportConfirm?.onConfirm()
          setPendingExportConfirm(undefined)
        }}
        onClose={() => setPendingExportConfirm(undefined)}
        visible={!!pendingExportConfirm}
      />
    </div>
  )
}
